import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { DEFAULT_MOTOR_LIBRARY, type Motor } from '@noumase/cylinder-calc';

const STORAGE_KEY = 'noumase.motorLibrary.v1';

function loadFromStorage(): Motor[] | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    // 老数据可能没有 model / frameSize 字段，这两个字段现已改为可选，直接透传。
    return parsed as Motor[];
  } catch {
    return null;
  }
}

function saveToStorage(motors: Motor[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(motors));
  } catch (e) {
    console.warn('Failed to save motor library to localStorage', e);
  }
}

function genId() {
  return `custom-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export type MotorDraft = Omit<Motor, 'id'>;

interface MotorLibraryContextValue {
  motors: Motor[];
  /** 当前库是否包含用户改动（true = 来自 localStorage，false = 内置默认） */
  customized: boolean;
  add: (draft: MotorDraft) => Motor;
  update: (id: string, patch: Partial<MotorDraft>) => void;
  remove: (id: string) => void;
  resetDefault: () => void;
  importMotors: (motors: Motor[]) => void;
  exportJson: () => string;
}

const Ctx = createContext<MotorLibraryContextValue | null>(null);

export function MotorLibraryProvider({ children }: { children: ReactNode }) {
  const [customized, setCustomized] = useState<boolean>(() => loadFromStorage() !== null);
  const [motors, setMotors] = useState<Motor[]>(
    () => loadFromStorage() ?? [...DEFAULT_MOTOR_LIBRARY],
  );

  useEffect(() => {
    if (customized) {
      saveToStorage(motors);
    }
  }, [motors, customized]);

  const add = useCallback((draft: MotorDraft): Motor => {
    const m: Motor = { ...draft, id: genId() };
    setMotors((prev) => [...prev, m]);
    setCustomized(true);
    return m;
  }, []);

  const update = useCallback((id: string, patch: Partial<MotorDraft>) => {
    setMotors((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
    setCustomized(true);
  }, []);

  const remove = useCallback((id: string) => {
    setMotors((prev) => prev.filter((m) => m.id !== id));
    setCustomized(true);
  }, []);

  const resetDefault = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setMotors([...DEFAULT_MOTOR_LIBRARY]);
    setCustomized(false);
  }, []);

  const importMotors = useCallback((incoming: Motor[]) => {
    setMotors([...incoming]);
    setCustomized(true);
  }, []);

  const exportJson = useCallback(() => JSON.stringify(motors, null, 2), [motors]);

  return (
    <Ctx.Provider
      value={{
        motors,
        customized,
        add,
        update,
        remove,
        resetDefault,
        importMotors,
        exportJson,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useMotorLibrary(): MotorLibraryContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useMotorLibrary must be used within MotorLibraryProvider');
  return v;
}
